import React, { useState } from 'react';
import { Title } from 'react-admin';
import {
  Card, CardContent, Grid, TextField, Button, Typography,
  CircularProgress, FormGroup, FormControlLabel, Checkbox
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';

const jsonPreStyle = {
  backgroundColor: '#f5f5f5',
  color: '#333333',
  padding: '12px',
  borderRadius: '4px',
  overflow: 'auto',
  fontSize: '12px',
  fontFamily: 'monospace',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  maxHeight: '500px',
  marginTop: '12px'
};

async function apiPost (path, body) {
  const url = `${window.nobid_endpoint}${path}`;
  const token = localStorage.getItem('token');
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  return response.json();
}

function ResultDisplay ({ result, loading, error }) {
  if (loading) return <CircularProgress size={24} style={{ marginTop: 12 }} />;
  if (error) return <Typography color="error" style={{ marginTop: 12 }}>{error}</Typography>;
  if (!result) return null;
  return <pre style={jsonPreStyle}>{JSON.stringify(result, null, 2)}</pre>;
}

function IpLookupCard () {
  const [ip, setIp] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ip.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await apiPost('/admin_tools/lookup_ip', { ip: ip.trim() });
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>IP Lookup</Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Look up IP metadata — ISP, org, ASN, datacenter/proxy flags (via ip-api.com)
        </Typography>
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <TextField
            label="IP Address"
            value={ip}
            onChange={e => setIp(e.target.value)}
            variant="outlined"
            size="small"
            placeholder="e.g. 8.8.8.8"
            style={{ flex: 1 }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || !ip.trim()}
            startIcon={<SearchIcon />}
          >
            Lookup
          </Button>
        </form>
        <ResultDisplay result={result} loading={loading} error={error} />
      </CardContent>
    </Card>
  );
}

function DomainLookupCard () {
  const [domain, setDomain] = useState('');
  const [checks, setChecks] = useState({ dns: true, web_content: true });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!domain.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const selectedChecks = Object.entries(checks).filter(([, v]) => v).map(([k]) => k);
      const data = await apiPost('/admin_tools/lookup_domain', {
        domain: domain.trim(),
        checks: selectedChecks.length > 0 ? selectedChecks : undefined
      });
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Domain Lookup</Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Check domain info — Tranco ranking, DNS resolution, web content signals
        </Typography>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <TextField
              label="Domain"
              value={domain}
              onChange={e => setDomain(e.target.value)}
              variant="outlined"
              size="small"
              placeholder="e.g. example.com"
              style={{ flex: 1 }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || !domain.trim()}
              startIcon={<SearchIcon />}
            >
              Lookup
            </Button>
          </div>
          <FormGroup row style={{ marginTop: '8px' }}>
            <FormControlLabel
              control={<Checkbox checked={checks.dns} onChange={e => setChecks(prev => ({ ...prev, dns: e.target.checked }))} size="small" />}
              label="DNS"
            />
            <FormControlLabel
              control={<Checkbox checked={checks.web_content} onChange={e => setChecks(prev => ({ ...prev, web_content: e.target.checked }))} size="small" />}
              label="Web Content"
            />
          </FormGroup>
        </form>
        <ResultDisplay result={result} loading={loading} error={error} />
      </CardContent>
    </Card>
  );
}

export const ToolsList = () => (
  <div style={{ padding: '16px' }}>
    <Title title="Tools" />
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <IpLookupCard />
      </Grid>
      <Grid item xs={12} md={6}>
        <DomainLookupCard />
      </Grid>
    </Grid>
  </div>
);
